// Faixas da aba Corte — renderClips / renderJcutAudio / drawWave do edvid:
// marcações, vídeo (takes com alças de trim, fantasma ao remover), áudio (onda do
// cut.mp4) e as faixas A1/A2 do J-cut, recolhidas por padrão.
import { useRef, useState, type RefObject } from 'react';
import type { EdvidCutState, EdvidNote } from '../../../types/videoEdit';
import {
  MIN_SEG, edgeInSpeech, fmt, type DraftTake, type LaidTake, type RenderedLaid,
} from './edvidTime';
import { fmtSpeed, speedOf } from './edvidTime';
import { TimelineShell, TrackRow, useTimeline } from './TimelineShell';
import { WaveformCanvas, useAudioPeaks } from './Waveform';
import { NotesLane } from './notes';
import type { Playhead } from './playhead';
import { IconAudio, IconCaret, IconFlag, IconVideo } from './icons';

interface Common {
  cut: EdvidCutState;
  draft: DraftTake[];
  dl: LaidTake[];
  rl: RenderedLaid[];
  total: number;
  editable: boolean;
  selected: number;
}

function Clips({ cut, draft, dl, rl, editable, selected, onSelect, onDraft, onReset }: Common & {
  onSelect: (i: number) => void;
  onDraft: (i: number, patch: Partial<DraftTake>) => void;
  onReset: (i: number) => void;
}) {
  const { pps, showTip, hideTip } = useTimeline();
  const drag = useRef<{ i: number; side: 'l' | 'r'; x0: number; r: DraftTake } | null>(null);
  const regions = cut.speechRegions || {};
  const videoTotal = cut.durationSec;

  const onHandleDown = (e: React.PointerEvent, i: number, side: 'l' | 'r') => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(i);
    drag.current = { i, side, x0: e.clientX, r: { ...draft[i] } };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* sintético */ }
  };
  const onHandleMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // o arrasto é em tempo da timeline (saída); na fonte vale × velocidade do take
    const dt = ((e.clientX - d.x0) / pps) * speedOf(draft[d.i] || {});
    const r = d.r;
    let start = r.start;
    let end = r.end;
    if (d.side === 'l') {
      start = Math.min(Math.max(0, r.start + dt), r.end - MIN_SEG);
    } else {
      end = Math.max(r.end + dt, r.start + MIN_SEG);
      const srcDur = cut.sourceDurations?.[r.source];
      if (srcDur) end = Math.min(end, srcDur);
    }
    onDraft(d.i, { start, end });
    const edge = d.side === 'l' ? start : end;
    const delta = d.side === 'l' ? start - r.orig.start : end - r.orig.end;
    const inSpeech = edgeInSpeech(regions[r.source], edge);
    showTip(e.clientX, e.clientY, (
      <span>
        {fmt(start)} → {fmt(end)} <span className={delta >= 0 ? 'text-emerald-300' : 'text-amber-300'}>({delta >= 0 ? '+' : ''}{delta.toFixed(2)}s)</span>
        {inSpeech && <span className="ml-2 font-sans font-bold text-amber-300">borda no meio da fala</span>}
      </span>
    ));
  };
  const onHandleUp = () => { drag.current = null; hideTip(); };

  return (
    <>
      {dl.map((r, i) => {
        if (r.removed) {
          return (
            <div
              key={i}
              data-tl-item
              onPointerDown={e => { e.stopPropagation(); onSelect(i); }}
              onDoubleClick={() => onReset(i)}
              className={`absolute bottom-1 top-1 cursor-pointer rounded-lg border border-dashed border-white/25 bg-white/5 opacity-50 grayscale ${selected === i ? 'ring-2 ring-indigo-400' : ''}`}
              style={{ left: r.out * pps, width: Math.max((r.orig.end - r.orig.start) * pps * 0.4, 34) }}
              title={`${r.beat} removido — selecione e aperte delete para restaurar`}
            >
              <svg className="absolute inset-0 h-full w-full text-white/30" preserveAspectRatio="none" viewBox="0 0 10 10" aria-hidden><path d="M0 10L10 0" stroke="currentColor" strokeWidth="0.4" /></svg>
            </div>
          );
        }
        const dirty = r.start !== r.orig.start || r.end !== r.orig.end;
        const warnL = r.start !== r.orig.start && edgeInSpeech(regions[r.source], r.start);
        const warnR = r.end !== r.orig.end && edgeInSpeech(regions[r.source], r.end);
        const strip = cut.filmstripUrl && rl[i]
          ? { backgroundImage: `url("${cut.filmstripUrl}")`, backgroundSize: `${videoTotal * pps}px 100%`, backgroundPosition: `${-rl[i].out * pps}px 0` }
          : undefined;
        return (
          <div
            key={i}
            data-tl-item
            onPointerDown={e => { e.stopPropagation(); onSelect(i); }}
            onDoubleClick={() => onReset(i)}
            className={`group absolute bottom-1 top-1 overflow-hidden rounded-lg border bg-indigo-500/20 ${selected === i ? 'z-[5] border-indigo-300 ring-2 ring-indigo-400' : dirty ? 'border-dashed border-amber-300/80' : 'border-indigo-400/40 hover:border-white/40'}`}
            style={{ left: r.out * pps, width: Math.max(r.dur * pps, 8), ...strip }}
            title={`${r.beat}${r.quote ? ` — “${r.quote}”` : ''}\n${fmt(r.start)} → ${fmt(r.end)} (${r.source})${r.reason ? `\n${r.reason}` : ''}\n\narraste as bordas · duplo-clique desfaz · delete remove`}
          >
            <div className={`pointer-events-none absolute inset-0 ${strip ? 'bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/50' : ''}`} />
            <div className="pointer-events-none relative flex h-full flex-col justify-between px-2 py-1">
              <span className={`truncate text-[10px] font-black uppercase tracking-wider text-indigo-100 ${r.dur * pps > 64 ? 'pr-9' : ''}`}>{r.beat || r.source}{speedOf(r) !== 1 ? ` · ${fmtSpeed(speedOf(r))}` : ''}</span>
              {r.quote && <span className="truncate text-[10px] text-slate-300/90">{r.quote}</span>}
            </div>
            {r.dur * pps > 64 && <span className="pointer-events-none absolute right-1.5 top-1 font-mono text-[9px] text-slate-300/80">{r.dur.toFixed(2)}s</span>}
            {editable && (['l', 'r'] as const).map(side => (
              <div
                key={side}
                onPointerDown={e => onHandleDown(e, i, side)}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                className={`absolute bottom-0 top-0 z-[2] w-2 cursor-ew-resize ${side === 'l' ? 'left-0' : 'right-0'} ${(side === 'l' ? warnL : warnR) ? 'bg-amber-400/80' : 'bg-transparent group-hover:bg-indigo-300/70'}`}
                title={(side === 'l' ? warnL : warnR) ? 'Esta borda caiu no meio de uma fala' : 'Arraste para aparar'}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

function AudioBlocks({ dl }: { dl: LaidTake[] }) {
  const { pps } = useTimeline();
  return (
    <>
      {dl.filter(r => !r.removed).map((r, i) => (
        <div key={i} className="absolute bottom-1.5 top-1.5 rounded-md border border-lime-400/30 bg-lime-500/10" style={{ left: r.aout * pps, width: Math.max(r.adur * pps, 4) }} />
      ))}
    </>
  );
}

function JcutLane({ dl, parity, fps, tailFrames }: { dl: LaidTake[]; parity: 0 | 1; fps: number; tailFrames: (i: number) => number }) {
  const { pps } = useTimeline();
  return (
    <>
      {dl.map((r, i) => {
        if (i % 2 !== parity || (r.removed && r.adur === 0)) return null;
        const tf = tailFrames(i);
        let tip = `${r.beat || r.source}\náudio ${r.adur.toFixed(2)}s`;
        if (r.lead > 1e-6) tip += `\nJ-cut: ${Math.round(r.lead * fps)}f (${Math.round(r.lead * 1000)}ms) de voz antes da imagem`;
        if (tf) tip += `\ncauda aparada ${tf}f`;
        return (
          <div
            key={i}
            className="absolute bottom-[3px] top-[3px] flex items-center overflow-hidden rounded-md border border-lime-400/40 bg-lime-500/15"
            style={{ left: r.aout * pps, width: Math.max(r.adur * pps, 6) }}
            title={tip}
          >
            {r.lead > 1e-6 && (
              <div
                className="absolute bottom-0 left-0 top-0 border-r border-lime-300/70"
                style={{ width: r.lead * pps, backgroundImage: 'repeating-linear-gradient(135deg, rgba(190,242,100,.45) 0 2px, transparent 2px 5px)' }}
              />
            )}
            <span className="relative truncate px-1.5 text-[9px] font-bold leading-none text-lime-100/90" style={{ marginLeft: r.lead * pps }}>{r.beat || r.source}</span>
          </div>
        );
      })}
    </>
  );
}

export function CutTimeline(props: Common & {
  panelRef: RefObject<HTMLDivElement>;
  pps: number;
  playhead: Playhead;
  onSeek: (t: number) => void;
  toMedia: (t: number) => number | null;
  notes: EdvidNote[];
  pendingIn: number | null;
  onOpenNote: (id: string) => void;
  onSelect: (i: number) => void;
  onDraft: (i: number, patch: Partial<DraftTake>) => void;
  onReset: (i: number) => void;
  version: unknown;
}) {
  const { cut, dl, total, panelRef, pps, playhead, onSeek, toMedia, notes, pendingIn, onOpenNote, version } = props;
  const peaks = useAudioPeaks(cut.url);
  const jcut = cut.edl.jcut_timeline;
  const hasJcut = !!jcut?.length;
  const [jcutOpen, setJcutOpen] = useState(() => {
    try { return localStorage.getItem('edvid.jcutOpen') === '1'; } catch { return false; }
  });
  const toggleJcut = () => {
    setJcutOpen(v => {
      try { localStorage.setItem('edvid.jcutOpen', v ? '0' : '1'); } catch { /* sem storage */ }
      return !v;
    });
  };
  const fps = cut.fps || cut.edl.fps || 30;

  return (
    <TimelineShell duration={total} pps={pps} panelRef={panelRef} playhead={playhead} onSeek={onSeek} className="shrink-0">
      <TrackRow icon={<IconFlag />} tone="rose" title="Marcações de ajuste" height={24}>
        <NotesLane notes={notes} pendingIn={pendingIn} onOpen={onOpenNote} />
      </TrackRow>
      <TrackRow icon={<IconVideo />} tone="indigo" title="Vídeo (takes)" height={58}>
        <Clips {...props} />
      </TrackRow>
      <TrackRow
        height={40}
        title="Áudio (mix)"
        labelExtra={(
          <button
            type="button"
            onClick={toggleJcut}
            disabled={!hasJcut}
            aria-expanded={hasJcut && jcutOpen}
            className="flex h-6 items-center gap-0.5 rounded-lg bg-lime-500/10 px-1 text-lime-300 hover:bg-lime-500/20 disabled:cursor-default disabled:hover:bg-lime-500/10"
            title={!hasJcut ? 'Áudio (mix)' : jcutOpen ? 'Recolher as faixas do J-cut (A1/A2)' : 'Expandir as faixas do J-cut (A1/A2)'}
          >
            <IconAudio />
            {hasJcut && <IconCaret width={10} height={10} className={`transition-transform ${jcutOpen ? 'rotate-180' : ''}`} />}
          </button>
        )}
      >
        {peaks.status === 'ready'
          ? <WaveformCanvas peaks={peaks.peaks} toMedia={toMedia} total={total} version={version} />
          : <AudioBlocks dl={dl} />}
        {peaks.status === 'loading' && <span className="pointer-events-none absolute right-2 top-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">lendo áudio…</span>}
      </TrackRow>
      {hasJcut && jcutOpen && (
        <>
          <TrackRow height={24} title="A1" labelExtra={<span className="text-[9px] font-black text-lime-300/70">A1</span>}>
            <JcutLane dl={dl} parity={0} fps={fps} tailFrames={i => jcut?.[i]?.tail_trim_frames || 0} />
          </TrackRow>
          <TrackRow height={24} title="A2" labelExtra={<span className="text-[9px] font-black text-lime-300/70">A2</span>}>
            <JcutLane dl={dl} parity={1} fps={fps} tailFrames={i => jcut?.[i]?.tail_trim_frames || 0} />
          </TrackRow>
        </>
      )}
    </TimelineShell>
  );
}
